/**
 * Message Service
 *
 * Handles API calls for messages
 */

import { EnhancedMessage } from "@/types/centralizedChat";
import { fetchUserBasicInfo } from "@/utils/user-helpers";
import { logger } from "@/utils/logger";
import { enhanceMessage } from "../utils/messageEnhancer";
import { MessagePageResult, MessageServiceContext } from "./types";

/**
 * Enhance messages with user information
 */
async function enhanceMessages(
  messages: any[],
  context: MessageServiceContext
): Promise<EnhancedMessage[]> {
  const enhanced: EnhancedMessage[] = [];
  const needsUserInfo: Array<{ message: any; index: number }> = [];

  for (let i = 0; i < messages.length; i++) {
    const message = messages[i];
    if (message.users) {
      enhanced[i] = enhanceMessage(message, context);
    } else {
      needsUserInfo.push({ message, index: i });
    }
  }

  // Batch fetch user info for messages that need it
  if (needsUserInfo.length > 0) {
    const userIds = [
      ...new Set(needsUserInfo.map(item => item.message.user_id)),
    ];
    const userInfoMap = new Map<string, any>();

    // Fetch user info in parallel
    await Promise.all(
      userIds.map(async userId => {
        try {
          const userInfo = await fetchUserBasicInfo(userId);
          userInfoMap.set(userId, {
            id: userId,
            full_name: userInfo.fullName,
          });
        } catch {
          logger.warn("Failed to fetch user info for message", {
            component: "messageService",
            userId,
          });
          userInfoMap.set(userId, {
            id: userId,
            full_name: null,
          });
        }
      })
    );

    // Enhance messages with fetched user info
    for (const { message, index } of needsUserInfo) {
      const userInfo = userInfoMap.get(message.user_id);
      enhanced[index] = enhanceMessage(
        { ...message, users: userInfo },
        context
      );
    }
  }

  return enhanced;
}

/**
 * Fetch messages for a conversation with pagination
 */
export async function fetchMessages(
  conversationId: string,
  page: number = 1,
  context: MessageServiceContext
): Promise<MessagePageResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  try {
    const response = await fetch(
      `/api/messages/paginated?conversationId=${conversationId}&page=${page}&limit=50`,
      {
        credentials: "include",
        headers: { "Cache-Control": "no-cache" },
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Failed to fetch messages: ${response.status}`);
    }

    const responseData = await response.json();
    const { messages: rawMessages, pagination: rawPagination } =
      responseData.data;

    if (!rawMessages || rawMessages.length === 0) {
      return {
        messages: [],
        pagination: {
          currentPage: page,
          hasMore: false,
          totalPages: 0,
          totalCount: 0,
        },
      };
    }

    // Enhance messages with user information
    const enhancedMessages = await enhanceMessages(rawMessages, context);

    // Messages come from API in descending order (newest first)
    // Reverse to display order (oldest first, newest last)
    const displayMessages = enhancedMessages.reverse();

    return {
      messages: displayMessages,
      pagination: {
        currentPage: rawPagination.page,
        hasMore: rawPagination.hasMore,
        totalPages: rawPagination.totalPages,
        totalCount: rawPagination.totalCount,
      },
    };
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error("Request timed out. Please try again.");
    }
    throw error;
  }
}

/**
 * Send a message
 */
export async function sendMessage(
  conversationId: string,
  content: string,
  context: MessageServiceContext,
  options?: { imagePath?: string | null; referencedTicketId?: string | null }
): Promise<EnhancedMessage> {
  const trimmedContent = content.trim();
  const imagePath = options?.imagePath || null;
  const referencedTicketId = options?.referencedTicketId || null;

  if (!trimmedContent && !imagePath) {
    throw new Error("Message must include text or an image");
  }

  const requestBody: {
    conversationId: string;
    content: string;
    imagePath?: string;
    referencedTicketId?: string;
  } = {
    conversationId,
    content: trimmedContent,
  };

  if (imagePath) {
    requestBody.imagePath = imagePath;
  }

  if (referencedTicketId) {
    requestBody.referencedTicketId = referencedTicketId;
  }

  const response = await fetch("/api/messages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new Error("Unable to send message. Please try again.");
  }

  const result = await response.json();
  if (!result.data?.message) {
    logger.error("Invalid message response structure", {
      component: "messageService",
      metadata: { result: JSON.stringify(result) },
    });
    throw new Error("Server response missing message data");
  }
  return enhanceMessage(result.data.message, context);
}
